import { CATEGORIES, type Category } from "./categories";

/**
 * COGM per SET, by product line — the rate card.
 *
 * THE STANDARD RATE, not a per-WIP figure. iSyn_WIPs carries a `cogm` column
 * of its own, and reading the subcon exposure off it produced about ₱20 a set
 * against a rate card that runs ₱715–1080 — two orders of magnitude out. That
 * column is measuring something else (or is stale on most rows), and a costing
 * screen built on it would have quietly understated every line by ~98%. The
 * rate card is the authority here.
 *
 * A FLAT RATE PER LINE is the deliberate simplification: a set of ELITE costs
 * what a set of ELITE costs, whatever bundle it arrived in. That is how the
 * floor quotes it, and it means a costing figure can be checked against this
 * table by hand rather than trusted.
 *
 * EDITABLE, AND STAMPED. The table below is the shipped card; the floor edits
 * it on screen and the result is stored in mfg settings (see
 * MfgSettingsService.getCogmRates). Rates move — a card that needs a deploy to
 * change is a card that goes stale and gets worked around in a spreadsheet
 * nobody else can see. What a stored card MUST carry is the day it took
 * effect and who set it, because a costing figure someone screenshots today
 * has to stay interpretable next quarter: a rate card with no date on it is
 * the reason nobody can reconcile last quarter's numbers.
 */
export const DEFAULT_COGM_PER_SET: Record<Category, number> = {
  CLASSIC: 760,
  ELITE: 810,
  ALTA: 900,
  PRIMO: 1030,
  "ELITE PRO": 1080,
  "NURSE UNIFORM": 715,
  "DOCTORS COAT": 1020,
};

/**
 * The shipped card, kept under its old name so the tests that pin the floor's
 * quoted numbers keep pinning them. Read `DEFAULT_COGM_PER_SET` in new code —
 * this one reads like "the rate", and once the card is editable it is only
 * ever "the rate we shipped with".
 */
export const COGM_PER_SET = DEFAULT_COGM_PER_SET;

/** When the shipped card was quoted. A stored card carries its own date. */
export const DEFAULT_RATES_EFFECTIVE = "2026-01-01";

/** A rate card: every product line priced, plus when and by whom. */
export interface ICogmRateCard {
  rates: Record<Category, number>;
  /** `YYYY-MM-DD` the card took effect. */
  effective: string;
  /** Who saved it. Empty on the shipped card — nobody saved it. */
  setBy: string;
  /** False for the shipped card, true once somebody has edited it. Screens say
   * which, so a figure is never quietly attributed to the wrong card. */
  stored: boolean;
}

export const SHIPPED_CARD: ICogmRateCard = {
  rates: DEFAULT_COGM_PER_SET,
  effective: DEFAULT_RATES_EFFECTIVE,
  setBy: "",
  stored: false,
};

/**
 * The rate for a line, or undefined when the WIP names no line at all.
 *
 * Undefined rather than a house average on purpose: an unclassified bundle is
 * a question, and pricing it at the mean is how the question stops being
 * asked. The screen counts those sets separately so the gap is visible.
 */
export function cogmRateFor(
  category: string | undefined,
  rates: Readonly<Record<string, number>> = DEFAULT_COGM_PER_SET,
): number | undefined {
  if (!category) return undefined;
  const v = rates[category];
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

/** Every line has a rate — a category with none would silently drop out of
 * every costing total. Exported so a test can assert it rather than trusting
 * that two literals were kept in step by hand. */
export function categoriesMissingRates(
  rates: Readonly<Record<string, number>> = DEFAULT_COGM_PER_SET,
): string[] {
  return CATEGORIES.filter((c) => typeof rates[c] !== "number");
}

// ─── Storage ────────────────────────────────────────────────────────────────

/**
 * Two-letter codes, so the whole card fits the settings list's 255-character
 * settingValue.
 *
 * Codes rather than positions: an index-keyed format silently reprices every
 * line the day somebody reorders CATEGORIES, and the damage would be invisible
 * — the totals still add up, they are just wrong. A code that no longer exists
 * is ignored on read instead, which loses one line loudly rather than
 * scrambling all seven quietly.
 */
export const RATE_CODE: Record<Category, string> = {
  CLASSIC: "CL",
  ELITE: "EL",
  ALTA: "AL",
  PRIMO: "PR",
  "ELITE PRO": "EP",
  "NURSE UNIFORM": "NU",
  "DOCTORS COAT": "DC",
};

const BY_CODE = new Map<string, Category>(
  (Object.entries(RATE_CODE) as [Category, string][]).map(([cat, code]) => [code, cat]),
);

/** What the settings list will take. Anything longer is refused, not cut. */
export const MAX_SETTING_VALUE = 255;

export const RATE_MIN = 0;
/** A ceiling, not a limit anyone will meet. It exists so a fat-fingered
 * 8100000 is refused at the form rather than showing up as a ₱6bn exposure
 * that somebody has to explain. */
export const RATE_MAX = 100000;

export type RateProblem = "not-a-number" | "negative" | "too-large" | "not-whole-centavo";

/** Whether one typed rate can be stored. */
export function validateRate(v: number): RateProblem | null {
  if (!Number.isFinite(v)) return "not-a-number";
  if (v < RATE_MIN) return "negative";
  if (v > RATE_MAX) return "too-large";
  // Two decimal places is what pesos have and what the screen prints. A rate
  // with more would render rounded and total unrounded, so the column would
  // stop adding up on screen.
  if (Math.abs(v * 100 - Math.round(v * 100)) > 1e-9) return "not-whole-centavo";
  return null;
}

/**
 * `v1|YYYY-MM-DD|CL:760|EL:810|…`
 *
 * Version tag first so a later format change is detectable rather than
 * mis-parsed. Throws past the 255-character cap: a truncated rate card parses
 * as a valid card with lines missing, which prices real bundles at nothing.
 * Refusing the save is recoverable; a silent short card is not.
 */
export function serialiseCogmRates(card: { rates: Record<Category, number>; effective: string }): string {
  const parts = CATEGORIES.map((c) => `${RATE_CODE[c]}:${trimNum(card.rates[c])}`);
  const out = `v1|${card.effective}|${parts.join("|")}`;
  if (out.length > MAX_SETTING_VALUE) {
    throw new Error(
      `Rate card is ${out.length} characters and the settings row holds ${MAX_SETTING_VALUE}. ` +
      `Nothing was saved — shorten the rates rather than losing a product line.`,
    );
  }
  return out;
}

/** `760` not `760.00`, and `760.5` not `760.50` — every character counts
 * against the 255, and the screen formats for display anyway. */
function trimNum(n: number): string {
  return String(Math.round(n * 100) / 100);
}

/**
 * Reads a stored card back, falling back per line.
 *
 * A line the string does not mention keeps its shipped rate rather than
 * becoming unpriced. Unpriced means "we do not know what this costs", and a
 * storage format that can manufacture that state turns a parsing hiccup into
 * a costing hole. Returns null only when the string is not a rate card at all.
 */
export function parseCogmRates(raw: string | null | undefined): ICogmRateCard | null {
  if (!raw) return null;
  const parts = raw.split("|");
  if (parts[0] !== "v1" || parts.length < 3) return null;
  const effective = /^\d{4}-\d{2}-\d{2}$/.test(parts[1]) ? parts[1] : DEFAULT_RATES_EFFECTIVE;
  const rates = { ...DEFAULT_COGM_PER_SET };
  let any = false;
  for (const p of parts.slice(2)) {
    const [code, v] = p.split(":");
    const cat = BY_CODE.get(code);
    if (!cat) continue;
    const n = Number(v);
    if (validateRate(n) !== null) continue;
    rates[cat] = n;
    any = true;
  }
  if (!any) return null;
  return { rates, effective, setBy: "", stored: true };
}

/** Lines whose stored rate differs from the one the app shipped with — what a
 * screen shows so an edited card is visibly edited, not just different. */
export function changedLines(card: ICogmRateCard): Category[] {
  return CATEGORIES.filter((c) => card.rates[c] !== DEFAULT_COGM_PER_SET[c]);
}
